import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { genreOptions, regionOptions, yearOptions, sortOptions, categoryList } from "@/mocks/categories";
import { filterDramas, type FilterState } from "@/utils/content";
import DramaCard from "@/components/feature/DramaCard";
import Breadcrumbs from "@/components/feature/Breadcrumbs";

const PAGE_SIZE = 8;

export default function Category() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialCat = searchParams.get("cat") || "全部";
  const initialSort = searchParams.get("sort") || "latest";

  const [filters, setFilters] = useState<FilterState>({
    category: initialCat,
    genre: "全部",
    year: "全部",
    region: "全部",
    sort: initialSort,
  });
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [panelOpen, setPanelOpen] = useState(false);

  useEffect(() => {
    const cat = searchParams.get("cat") || "全部";
    const sort = searchParams.get("sort") || "latest";
    setFilters((prev) => ({ ...prev, category: cat, sort }));
  }, [searchParams]);

  useEffect(() => {
    setVisible(PAGE_SIZE);
  }, [filters]);

  const results = useMemo(() => filterDramas(filters), [filters]);
  const shown = results.slice(0, visible);

  const updateFilter = (patch: Partial<FilterState>) => {
    setFilters((prev) => ({ ...prev, ...patch }));
  };

  const setCategory = (cat: string) => {
    updateFilter({ category: cat });
    if (cat === "全部") {
      searchParams.delete("cat");
    } else {
      searchParams.set("cat", cat);
    }
    setSearchParams(searchParams, { replace: true });
  };

  const resetFilters = () => {
    setFilters({ category: "全部", genre: "全部", year: "全部", region: "全部", sort: "latest" });
    setSearchParams({}, { replace: true });
  };

  const activeCat = categoryList.find((item) => item.key === filters.category);

  const filterGroups = [
    { label: "类型", key: "genre" as const, options: genreOptions },
    { label: "年代", key: "year" as const, options: yearOptions },
    { label: "地区", key: "region" as const, options: regionOptions },
  ];

  return (
    <div className="mx-auto w-full max-w-[1280px] px-4 py-6 md:px-6 md:py-8">
      <title>{`${activeCat ? activeCat.name : "全部影视"}分类 - 善良的嫂子影视站`}</title>
      <meta
        name="description"
        content={`善良的嫂子影视分类页，汇集${activeCat ? activeCat.name : "电视剧、电影、综艺"}等影视作品，支持按类型、年代、地区筛选与排序，快速发现优质影视资源。`}
      />
      <meta name="keywords" content="影视分类,电视剧,电影,综艺,善良的嫂子,影视筛选" />

      <Breadcrumbs items={[{ label: "影视分类" }]} />

      <div className="mt-4 flex flex-col gap-4 border-b border-background-200 pb-5 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            {activeCat ? `${activeCat.name}分类` : "全部影视"}
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-foreground-500">
            {activeCat ? activeCat.desc : "浏览全部收录的影视作品，可通过下方筛选与排序快速定位你想看的内容。"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-foreground-500">排序</span>
          <div className="flex items-center gap-1 rounded-full bg-background-100 p-1">
            {sortOptions.map((option) => (
              <button
                key={option.key}
                type="button"
                onClick={() => updateFilter({ sort: option.key })}
                className={`whitespace-nowrap rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${
                  filters.sort === option.key
                    ? "bg-primary-500 text-background-50"
                    : "text-foreground-600 hover:text-primary-600"
                }`}
              >
                {option.name}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-5 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => setCategory("全部")}
          className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
            filters.category === "全部"
              ? "bg-primary-500 text-background-50"
              : "bg-background-100 text-foreground-600 hover:text-primary-600"
          }`}
        >
          全部
        </button>
        {categoryList.map((cat) => (
          <button
            key={cat.key}
            type="button"
            onClick={() => setCategory(cat.key)}
            className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
              filters.category === cat.key
                ? "bg-primary-500 text-background-50"
                : "bg-background-100 text-foreground-600 hover:text-primary-600"
            }`}
          >
            {cat.name}
          </button>
        ))}
      </div>

      <button
        type="button"
        onClick={() => setPanelOpen((prev) => !prev)}
        className="mt-5 flex w-full items-center justify-between rounded-lg border border-background-200 bg-background-50 px-4 py-3 text-sm font-medium text-foreground-800 md:hidden"
      >
        <span className="flex items-center gap-2">
          <span className="flex h-4 w-4 items-center justify-center"><i className="ri-filter-3-line"></i></span>
          筛选条件
        </span>
        <span className="flex h-4 w-4 items-center justify-center">
          <i className={panelOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}></i>
        </span>
      </button>

      <div className={`${panelOpen ? "block" : "hidden"} mt-3 rounded-lg border border-background-200 bg-background-50 p-4 md:mt-5 md:block md:p-5`}>
        <div className="flex flex-col gap-4">
          {filterGroups.map((group) => (
            <div key={group.key} className="flex flex-col gap-2 sm:flex-row sm:items-start">
              <span className="w-12 shrink-0 pt-1 text-sm font-medium text-foreground-500">{group.label}</span>
              <div className="flex flex-wrap gap-2">
                {group.options.map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => updateFilter({ [group.key]: option } as Partial<FilterState>)}
                    className={`whitespace-nowrap rounded-md px-3 py-1.5 text-sm transition-colors ${
                      filters[group.key] === option
                        ? "bg-secondary-500 text-background-50"
                        : "bg-background-100 text-foreground-600 hover:bg-secondary-100 hover:text-secondary-900"
                    }`}
                  >
                    {option}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="mt-4 flex items-center justify-between border-t border-background-200 pt-4">
          <span className="text-sm text-foreground-500">
            共 <strong className="text-primary-600">{results.length}</strong> 部作品
          </span>
          <button
            type="button"
            onClick={resetFilters}
            className="flex items-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-3 py-1.5 text-sm text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-600"
          >
            <span className="flex h-4 w-4 items-center justify-center"><i className="ri-refresh-line"></i></span>
            重置筛选
          </button>
        </div>
      </div>

      {shown.length > 0 ? (
        <>
          <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-4 xl:grid-cols-5">
            {shown.map((drama) => (
              <DramaCard key={drama.id} drama={drama} />
            ))}
          </div>
          {visible < results.length && (
            <div className="mt-8 flex justify-center">
              <button
                type="button"
                onClick={() => setVisible((prev) => prev + PAGE_SIZE)}
                className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-6 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
              >
                <span className="flex h-4 w-4 items-center justify-center"><i className="ri-add-line"></i></span>
                加载更多
              </button>
            </div>
          )}
        </>
      ) : (
        <div className="mt-8 flex flex-col items-center justify-center rounded-lg border border-background-200 bg-background-50 py-16">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-300">
            <i className="ri-search-eye-line text-3xl"></i>
          </span>
          <p className="mt-4 font-heading text-lg font-semibold text-foreground-800">没有找到匹配的影视内容</p>
          <p className="mt-1 text-sm text-foreground-500">试试调整筛选条件，或重置后重新浏览</p>
          <button
            type="button"
            onClick={resetFilters}
            className="mt-5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            重置筛选条件
          </button>
        </div>
      )}
    </div>
  );
}